import { useState, useEffect, useCallback, useMemo } from "react";
import { TrendingUp, Plus, Trash2, X, Search, ChevronDown, ChevronUp, Link2, Package, Eye, Check } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { CardImage } from "./CardImage";
import { PriceHistoryChart } from "./PriceHistoryChart";
import {
  searchGlobal, fetchInvestments, createInvestment, updateInvestment, deleteInvestment,
  fetchSealedProducts, createSealedProduct, updateSealedProduct,
  addSealedPrice, fetchSealedPriceHistory, fetchCardPriceHistory, searchCardmarketSealed,
  fetchCardmarketSealedSeen,
} from "../lib/api";
import { eur } from "../lib/prices";
import { setAsset, handleSetAssetError } from "../lib/images";

// Vue « Investissement » (2026-09-28, réservée aux comptes admin) — un
// portefeuille à PART de la collection perso (jamais mêlé aux compteurs/à la
// complétion habituels), cartes ET produits scellés, avec prix d'achat, cote
// suivie, note libre, note sur 10 et horizon court/moyen/long terme.
//
// Cote d'une carte : le même prix Cardmarket/TCGdex que partout ailleurs
// (recalculé côté serveur, jamais stocké). Cote d'un produit scellé : AUCUNE
// source automatique n'existe (TCGdex ne connaît pas les produits scellés)
// — relevé manuel ici, ou capture Œil de Sauron sur la fiche Cardmarket une
// fois le produit LIÉ à son idProduct Cardmarket.
//
// Refonte du 2026-10-03 après le premier essai réel : (1) le catalogue
// Cardmarket est en anglais, la recherche accepte donc un lien de fiche
// collé tel quel ou un nom en français traduit côté serveur ; (2) les fiches
// visitées avec Œil de Sauron sont proposées « vues récemment » pour lier en
// un clic ; (3) un produit sans photo montre le logo de son extension, plus
// jamais un texte de remplacement qui clignote ; (4) un produit se corrige
// après coup (nom, catégorie, photo, liaison).
const HORIZON_LABEL = { court: "Court terme", moyen: "Moyen terme", long: "Long terme" };
const HORIZONS = ["court", "moyen", "long"];
const SEALED_CATEGORIES = ["Display", "Booster", "ETB", "Coffret", "Blister", "Tin", "Autre"];
const CARDMARKET_URL_RE = /cardmarket\.com\/[a-z]{2}\/Pokemon\/Products\//i;

const Stat = ({ label, value, tone }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`text-xl font-bold ${tone || "text-white"}`}>{value}</div>
  </div>
);

// Illustration d'un produit scellé : photo du produit (saisie, ou relevée
// par Œil de Sauron sur la fiche Cardmarket) → logo de l'extension → icône.
// Volontairement PAS CardImage : ce composant est pensé pour les scans de
// cartes (cache hors ligne, retentative /high.webp, dos de carte en repli),
// aucune de ces règles n'a de sens pour la photo d'une boîte.
const SealedThumb = ({ item, className }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [item.image_url]);
  const photo = !failed && item.image_url ? item.image_url : null;
  if (photo) {
    return (
      <img src={photo} alt="" loading="lazy" onError={() => setFailed(true)}
        className={`${className} object-contain rounded bg-zinc-950 ring-1 ring-white/10`} />
    );
  }
  return (
    <div className={`${className} rounded bg-zinc-800/80 ring-1 ring-white/10 flex items-center justify-center p-1.5`}>
      {item.set_logo ? (
        <img src={setAsset(item.set_logo)} alt="" onError={handleSetAssetError} className="max-w-full max-h-full object-contain" />
      ) : (
        <Package className="w-5 h-5 text-zinc-500" />
      )}
    </div>
  );
};

// Recherche d'une carte réelle à ajouter au portefeuille — réutilise la
// recherche globale déjà existante (donc limitée aux cartes déjà croisées
// au moins une fois dans l'app, comme partout où /search est utilisé).
const CardPicker = ({ onPick }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      searchGlobal(query, 10).then(d => setResults(d.cards || [])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input placeholder="Nom de la carte…" className="search-input pl-9" value={q}
          onChange={(e) => setQ(e.target.value)} autoFocus />
      </div>
      {q.trim().length >= 2 && (
        <div className="mt-2 max-h-64 overflow-y-auto border border-zinc-800 rounded-lg divide-y divide-zinc-800">
          {loading && <div className="p-3 text-sm text-zinc-500">Recherche…</div>}
          {!loading && results.length === 0 && (
            <div className="p-3 text-sm text-zinc-500">
              Aucune carte trouvée — seules les cartes déjà ouvertes une fois dans l'app sont indexées.
            </div>
          )}
          {results.map(c => (
            <button key={c.id} onClick={() => onPick(c)}
              className="w-full flex items-center gap-3 p-2 text-left hover:bg-zinc-800/60">
              {c.image ? (
                <CardImage card={c} quality="low" className="w-9 h-[50px] object-cover rounded shrink-0" />
              ) : <div className="w-9 h-[50px] rounded bg-zinc-800 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white truncate">{c.name}</div>
                <div className="text-xs text-zinc-500 truncate">{c.set_name} · #{c.localId}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const shortCategory = (c) => (c || "").replace("Pokémon ", "");

// Liaison d'un produit scellé à sa fiche Cardmarket (catalogue PUBLIC,
// gratuit, pas de scraping). C'est ce qui permet (1) un lien exact vers la
// fiche, (2) à Œil de Sauron de reconnaître la page quand l'admin la visite
// et d'y relever prix + photo tout seul. Trois façons de trouver la fiche :
//   - coller l'URL de la fiche Cardmarket (correspondance exacte) ;
//   - taper le nom, en français (« display Flammes Obsidiennes ») ou en
//     anglais tel qu'écrit sur Cardmarket (« Obsidian Flames Booster Box ») ;
//   - choisir une fiche « vue récemment » avec Œil de Sauron.
const CardmarketSealedPicker = ({ onPick, autoFocus = false }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seen, setSeen] = useState([]);

  useEffect(() => { fetchCardmarketSealedSeen().then(setSeen).catch(() => setSeen([])); }, []);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      searchCardmarketSealed(query).then(setResults).catch(() => setResults([])).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const isUrl = CARDMARKET_URL_RE.test(q);

  return (
    <div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input placeholder="Lien de la fiche Cardmarket, ou nom du produit (français ou anglais)…"
          className="search-input pl-9" value={q} onChange={(e) => setQ(e.target.value)} autoFocus={autoFocus} />
      </div>
      {q.trim().length < 2 && seen.length > 0 && (
        <div className="mt-2 border border-zinc-800 rounded-lg overflow-hidden">
          <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-950/60 flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" />Vues récemment avec Œil de Sauron
          </div>
          <div className="divide-y divide-zinc-800 max-h-56 overflow-y-auto">
            {seen.map(s => (
              <button key={s.cardmarket_id} onClick={() => onPick(s)}
                className="w-full flex items-center gap-3 p-2 text-left hover:bg-zinc-800/60">
                <SealedThumb item={s} className="w-10 h-10 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{s.name}</div>
                  <div className="text-xs text-zinc-500 truncate">
                    {shortCategory(s.category)}{s.last_price != null ? ` · vu à ${eur(s.last_price)}` : ""}
                  </div>
                </div>
                <span className="text-xs text-amber-400 shrink-0">Lier</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {q.trim().length >= 2 && (
        <div className="mt-2 max-h-56 overflow-y-auto border border-zinc-800 rounded-lg divide-y divide-zinc-800">
          {loading && <div className="p-3 text-sm text-zinc-500">Recherche…</div>}
          {!loading && results.length === 0 && (
            <div className="p-3 text-sm text-zinc-500">
              {isUrl
                ? "Ce lien ne correspond à aucune fiche du catalogue Cardmarket (vérifie qu'il s'agit bien d'une fiche produit, pas d'une catégorie)."
                : "Aucun produit trouvé. Essaie avec moins de mots, ou colle directement le lien de la fiche Cardmarket."}
            </div>
          )}
          {results.map(r => (
            <button key={r.cardmarket_id} onClick={() => onPick(r)}
              className="w-full flex items-center justify-between gap-3 p-2.5 text-left hover:bg-zinc-800/60">
              <span className="text-sm text-white truncate flex items-center gap-2">
                {r.exact && <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}{r.name}
              </span>
              <span className="text-xs text-zinc-500 shrink-0">{shortCategory(r.category)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const LinkedBadge = ({ name, url, onChange }) => (
  <div className="flex items-center justify-between gap-2 bg-zinc-950 border border-emerald-500/30 rounded-md px-3 py-2 text-sm">
    <span className="text-white truncate flex items-center gap-2 min-w-0">
      <Link2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
      <span className="truncate">{name}</span>
    </span>
    <span className="flex items-center gap-3 shrink-0">
      {url && <a href={url} target="_blank" rel="noreferrer" className="text-xs text-sky-400 hover:underline">Fiche</a>}
      <button onClick={onChange} className="text-xs text-zinc-500 hover:text-white">Changer</button>
    </span>
  </div>
);

const AddInvestmentForm = ({ kind, pickedCard, sealedProducts, onCreateSealed, onSubmit, onCancel }) => {
  const [sealedMode, setSealedMode] = useState("new"); // "existing" | "new"
  const [sealedId, setSealedId] = useState("");
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("Autre");
  const [newImage, setNewImage] = useState("");
  const [imageNotice, setImageNotice] = useState("");
  const [cmPick, setCmPick] = useState(null); // fiche Cardmarket choisie, optionnel
  const [purchasePrice, setPurchasePrice] = useState("");
  // Quantité (demande utilisateur du 2026-10-03) : « 6 boosters » = une seule
  // ligne avec un multiplicateur, pas 6 ajouts à la main. Prix PAR UNITÉ.
  const [quantity, setQuantity] = useState("1");
  const [purchaseDate, setPurchaseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [rating, setRating] = useState("");
  const [horizon, setHorizon] = useState("moyen");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Sans produit existant, pas de choix à faire : on part sur « nouveau ».
    if (kind === "sealed" && sealedProducts.length === 0) setSealedMode("new");
  }, [kind, sealedProducts.length]);

  const qty = Math.max(1, parseInt(quantity, 10) || 1);
  const canSubmit = purchasePrice !== "" && (kind === "card"
    ? !!pickedCard
    : (sealedMode === "existing" ? !!sealedId : newName.trim().length > 0));

  // Un lien de fiche Cardmarket collé dans le champ « photo » (seul champ
  // où coller un lien dans la 1ʳᵉ version) : on le bascule en liaison.
  const handleImageChange = async (value) => {
    setNewImage(value);
    setImageNotice("");
    if (!CARDMARKET_URL_RE.test(value)) return;
    try {
      const hits = await searchCardmarketSealed(value.trim());
      if (hits.length > 0) {
        setCmPick(hits[0]);
        setNewImage("");
        setImageNotice(`Ce lien est une fiche Cardmarket, pas une image : produit lié à « ${hits[0].name} ».`);
        if (!newName.trim()) setNewName(hits[0].name);
      } else {
        setImageNotice("Ce lien est une fiche Cardmarket, pas une image — et elle n'est pas dans le catalogue.");
      }
    } catch { /* la recherche a échoué : on laisse la saisie telle quelle */ }
  };

  const pickCardmarket = (r) => {
    setCmPick(r);
    if (!newName.trim()) setNewName(r.name);
  };

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    try {
      let sealed_product_id = sealedId;
      if (kind === "sealed" && sealedMode === "new") {
        const created = await onCreateSealed({
          name: newName.trim(), category: newCategory, image_url: newImage.trim() || null,
          cardmarket_id: cmPick?.cardmarket_id || null,
        });
        sealed_product_id = created.id;
      }
      await onSubmit({
        kind,
        card_id: kind === "card" ? pickedCard.id : undefined,
        sealed_product_id: kind === "sealed" ? sealed_product_id : undefined,
        purchase_price: parseFloat(purchasePrice),
        quantity: qty,
        purchase_date: purchaseDate,
        note,
        rating: rating === "" ? null : parseInt(rating, 10),
        horizon,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mt-3 space-y-3">
      {kind === "sealed" && (
        <div>
          {sealedProducts.length > 0 && (
            <div className="flex gap-2 mb-2">
              <button onClick={() => setSealedMode("new")}
                className={`text-xs px-2.5 py-1 rounded-full border ${sealedMode === "new" ? "bg-zinc-700 border-zinc-600 text-white" : "border-zinc-700 text-zinc-400"}`}>
                + Nouveau produit
              </button>
              <button onClick={() => setSealedMode("existing")}
                className={`text-xs px-2.5 py-1 rounded-full border ${sealedMode === "existing" ? "bg-zinc-700 border-zinc-600 text-white" : "border-zinc-700 text-zinc-400"}`}>
                Produit déjà créé
              </button>
            </div>
          )}
          {sealedMode === "existing" ? (
            <select value={sealedId} onChange={(e) => setSealedId(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white">
              <option value="">— Choisir un produit —</option>
              {sealedProducts.map(sp => <option key={sp.id} value={sp.id}>{sp.name} ({sp.category})</option>)}
            </select>
          ) : (
            <div className="space-y-2">
              <div>
                <label className="text-xs text-zinc-500 block mb-1 flex items-center gap-1.5">
                  <Link2 className="w-3.5 h-3.5" />Fiche Cardmarket — pour le lien exact et le suivi du prix par Œil de Sauron
                </label>
                {cmPick ? (
                  <LinkedBadge name={cmPick.name} onChange={() => setCmPick(null)} />
                ) : (
                  <CardmarketSealedPicker onPick={pickCardmarket} autoFocus />
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="sm:col-span-2">
                  <label className="text-xs text-zinc-500 block mb-1">Nom affiché dans le portefeuille</label>
                  <Input placeholder="Ex. Display Flammes Obsidiennes" value={newName}
                    onChange={(e) => setNewName(e.target.value)} className="search-input" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Type</label>
                  <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)}
                    className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white h-9">
                    {SEALED_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-500 block mb-1">
                  Photo (URL d'une image, optionnel) — sinon : photo relevée par Œil de Sauron sur la fiche, ou logo de l'extension
                </label>
                <Input placeholder="https://…/photo.jpg" value={newImage}
                  onChange={(e) => handleImageChange(e.target.value)} className="search-input" />
                {imageNotice && <div className="text-xs text-amber-400 mt-1">{imageNotice}</div>}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div>
          <label className="text-xs text-zinc-500">Prix d'achat (l'unité)</label>
          <Input type="number" step="0.01" placeholder="0.00 €" value={purchasePrice}
            onChange={(e) => setPurchasePrice(e.target.value)} className="search-input" />
        </div>
        <div>
          <label className="text-xs text-zinc-500">Quantité</label>
          <Input type="number" min="1" step="1" value={quantity}
            onChange={(e) => setQuantity(e.target.value)} className="search-input" />
          {qty > 1 && purchasePrice !== "" && (
            <div className="text-[11px] text-zinc-500 mt-1">= {eur(qty * parseFloat(purchasePrice || 0))} au total</div>
          )}
        </div>
        <div>
          <label className="text-xs text-zinc-500">Date d'achat</label>
          <Input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} className="search-input" />
        </div>
        <div>
          <label className="text-xs text-zinc-500">Note /10</label>
          <select value={rating} onChange={(e) => setRating(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white h-9">
            <option value="">—</option>
            {Array.from({ length: 11 }, (_, i) => i).map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-zinc-500">Horizon</label>
          <select value={horizon} onChange={(e) => setHorizon(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white h-9">
            {HORIZONS.map(h => <option key={h} value={h}>{HORIZON_LABEL[h]}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs text-zinc-500">Note libre</label>
        <Textarea placeholder="Pourquoi cet achat, ce que tu surveilles…" value={note}
          onChange={(e) => setNote(e.target.value)} className="bg-zinc-950 border-zinc-700 text-white text-sm" rows={2} />
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" className="text-zinc-400" onClick={onCancel}>Annuler</Button>
        <Button disabled={!canSubmit || saving} onClick={submit} className="bg-amber-600 hover:bg-amber-500 text-white">
          {saving ? "Ajout…" : "Ajouter au portefeuille"}
        </Button>
      </div>
    </div>
  );
};

// Édition d'un produit scellé après coup (nom, type, photo, liaison
// Cardmarket) — manquait dans la 1ʳᵉ version : une erreur de saisie
// obligeait à supprimer et recréer la ligne.
const SealedProductEditor = ({ item, onSave }) => {
  const [name, setName] = useState(item.name || "");
  const [category, setCategory] = useState(item.category || "Autre");
  const [imageUrl, setImageUrl] = useState(item.image_url || "");
  const [linking, setLinking] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(item.name || "");
    setCategory(item.category || "Autre");
    setImageUrl(item.image_url || "");
  }, [item.sealed_product_id, item.name, item.category, item.image_url]);

  const dirty = name.trim() !== (item.name || "") || category !== (item.category || "Autre")
    || imageUrl.trim() !== (item.image_url || "");

  const save = async () => {
    if (!dirty || !name.trim() || saving) return;
    setSaving(true);
    try {
      await onSave(item.sealed_product_id, { name: name.trim(), category, image_url: imageUrl.trim() || null });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="text-xs text-zinc-500 flex items-center gap-1.5"><Package className="w-3.5 h-3.5" />Produit scellé</div>
      {item.cardmarket_id ? (
        linking ? (
          <div>
            <CardmarketSealedPicker autoFocus onPick={async (r) => { await onSave(item.sealed_product_id, { cardmarket_id: r.cardmarket_id }); setLinking(false); }} />
            <button onClick={() => setLinking(false)} className="text-xs text-zinc-500 hover:text-white mt-1">Annuler</button>
          </div>
        ) : (
          <LinkedBadge name={item.cardmarket_name || `Fiche Cardmarket #${item.cardmarket_id}`}
            url={item.cardmarket_url} onChange={() => setLinking(true)} />
        )
      ) : (
        <div>
          <div className="text-xs text-amber-400/90 mb-1 flex items-center gap-1.5">
            <Link2 className="w-3.5 h-3.5" />Pas encore lié à Cardmarket : Œil de Sauron ne peut pas relever son prix.
          </div>
          <CardmarketSealedPicker onPick={(r) => onSave(item.sealed_product_id, { cardmarket_id: r.cardmarket_id })} />
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="search-input sm:col-span-2" placeholder="Nom" />
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          className="bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white h-9">
          {SEALED_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          {!SEALED_CATEGORIES.includes(category) && <option value={category}>{category}</option>}
        </select>
        <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className="search-input sm:col-span-3"
          placeholder="Photo (URL d'une image) — vide = photo Œil de Sauron ou logo de l'extension" />
      </div>
      {dirty && (
        <div className="flex justify-end">
          <Button size="sm" disabled={!name.trim() || saving} onClick={save} className="bg-zinc-700 hover:bg-zinc-600 text-white">
            {saving ? "Enregistrement…" : "Enregistrer le produit"}
          </Button>
        </div>
      )}
    </div>
  );
};

const InvestmentRow = ({ item, onUpdate, onDelete, onRecordSealedPrice, onUpdateSealed }) => {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState(item.note || "");
  const [priceInput, setPriceInput] = useState("");
  const [history, setHistory] = useState(null);

  useEffect(() => setNote(item.note || ""), [item.id, item.note]);

  const loadHistory = useCallback(async () => {
    if (item.kind === "card") {
      const points = await fetchCardPriceHistory(item.card_id);
      setHistory(points.map(p => ({ date: p.date, trend: item.variant === "reverse" ? p.trend_holo : p.trend, avg1: p.avg1 })));
    } else {
      const points = await fetchSealedPriceHistory(item.sealed_product_id);
      setHistory(points.map(p => ({ date: p.date, trend: p.price })));
    }
  }, [item]);

  // La cote d'un scellé peut changer sans qu'on rouvre la ligne (capture Œil
  // de Sauron, liaison qui reprend un prix vu) : recharger l'historique
  // quand elle bouge et que la ligne est dépliée.
  useEffect(() => { if (expanded) loadHistory(); }, [expanded, item.current_price, loadHistory]);

  const toggleExpand = () => setExpanded(e => !e);

  const gainColor = item.gain == null ? "text-zinc-500" : item.gain >= 0 ? "text-emerald-400" : "text-red-400";

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3.5">
      <div className="flex items-center gap-3">
        {item.kind === "sealed" ? (
          <SealedThumb item={item} className="w-14 h-[68px] shrink-0" />
        ) : item.image ? (
          <CardImage card={item} quality="low" className="w-12 h-[68px] object-cover rounded shrink-0 ring-1 ring-white/10" />
        ) : (
          <div className="w-12 h-[68px] rounded bg-zinc-800 shrink-0 flex items-center justify-center text-[10px] text-zinc-600">?</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white truncate">{item.name}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border shrink-0 ${item.kind === "sealed" ? "border-amber-500/40 text-amber-400" : "border-sky-500/40 text-sky-400"}`}>
              {item.kind === "sealed" ? "Scellé" : "Carte"}
            </span>
            {item.kind === "sealed" && item.cardmarket_id && (
              <span className="text-[10px] text-emerald-400/90 flex items-center gap-1 shrink-0" title={item.cardmarket_name || ""}>
                <Link2 className="w-3 h-3" />Cardmarket
              </span>
            )}
          </div>
          <div className="text-xs text-zinc-500 truncate">
            {item.kind === "card" ? `${item.set_name || ""} · #${item.local_id || ""}` : item.category}
            {" · "}{item.purchase_date}{" · "}
            {item.quantity > 1
              ? <><b className="text-zinc-300">{item.quantity} ×</b> {eur(item.purchase_price)} = {eur(item.quantity * item.purchase_price)}</>
              : `achetée ${eur(item.purchase_price)}`}
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <Stat label={item.quantity > 1 ? `Cote ×${item.quantity}` : "Cote"}
            value={item.current_price != null ? eur(item.current_price * (item.quantity || 1)) : "—"} />
          <Stat label="+/- value" value={item.gain != null ? `${item.gain >= 0 ? "+" : ""}${eur(item.gain)}` : "—"} tone={gainColor} />
          <Stat label="%" value={item.gain_pct != null ? `${item.gain_pct >= 0 ? "+" : ""}${item.gain_pct}%` : "—"} tone={gainColor} />
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={toggleExpand} className="p-1.5 rounded text-zinc-500 hover:text-white hover:bg-zinc-800">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button onClick={() => onDelete(item.id)} className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-zinc-800">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-zinc-800 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Note /10</label>
              <select value={item.rating ?? ""} onChange={(e) => onUpdate(item.id, { rating: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                className="bg-zinc-950 border border-zinc-700 rounded-md px-2 py-1 text-sm text-white">
                <option value="">—</option>
                {Array.from({ length: 11 }, (_, i) => i).map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Horizon</label>
              <select value={item.horizon} onChange={(e) => onUpdate(item.id, { horizon: e.target.value })}
                className="bg-zinc-950 border border-zinc-700 rounded-md px-2 py-1 text-sm text-white">
                {HORIZONS.map(h => <option key={h} value={h}>{HORIZON_LABEL[h]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Quantité</label>
              <Input type="number" min="1" step="1" defaultValue={item.quantity || 1} key={`q${item.id}-${item.quantity}`}
                onBlur={(e) => { const q = Math.max(1, parseInt(e.target.value, 10) || 1); if (q !== (item.quantity || 1)) onUpdate(item.id, { quantity: q }); }}
                className="search-input h-8 w-20" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Prix d'achat (l'unité)</label>
              <Input type="number" step="0.01" defaultValue={item.purchase_price} key={`p${item.id}-${item.purchase_price}`}
                onBlur={(e) => { const p = parseFloat(e.target.value); if (!Number.isNaN(p) && p !== item.purchase_price) onUpdate(item.id, { purchase_price: p }); }}
                className="search-input h-8 w-28" />
            </div>
            {item.kind === "sealed" && (
              <div>
                <label className="text-xs text-zinc-500 block mb-1">Relever un prix à la main</label>
                <div className="flex gap-2">
                  <Input type="number" step="0.01" placeholder="0.00 €" value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)} className="search-input h-9 w-28" />
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-500 text-white" onClick={async () => {
                    if (!priceInput) return;
                    await onRecordSealedPrice(item.sealed_product_id, parseFloat(priceInput));
                    setPriceInput("");
                  }}>Enregistrer</Button>
                </div>
              </div>
            )}
          </div>

          {item.kind === "sealed" && <SealedProductEditor item={item} onSave={onUpdateSealed} />}

          <div>
            <label className="text-xs text-zinc-500 block mb-1">Note libre</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
              onBlur={() => { if (note !== (item.note || "")) onUpdate(item.id, { note }); }}
              className="bg-zinc-950 border-zinc-700 text-white text-sm" rows={2} />
          </div>

          <div>
            <label className="text-xs text-zinc-500 block mb-1">Historique de la cote</label>
            {history === null ? (
              <div className="text-xs text-zinc-600">Chargement…</div>
            ) : history.length < 2 ? (
              <div className="text-xs text-zinc-600">
                {history.length === 1
                  ? `Un seul relevé pour l'instant (${eur(history[0].trend)}) — la courbe apparaîtra au deuxième.`
                  : item.kind === "sealed" && !item.cardmarket_id
                    ? "Aucun relevé. Lie le produit à sa fiche Cardmarket : chaque visite avec Œil de Sauron ajoutera un point."
                    : "Pas encore de relevé."}
              </div>
            ) : (
              <PriceHistoryChart points={history} secondaryLabel="" secondaryColor="transparent" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const InvestmentView = () => {
  const [items, setItems] = useState(null);
  const [sealedProducts, setSealedProducts] = useState([]);
  const [tab, setTab] = useState("tout");
  const [addingKind, setAddingKind] = useState(null); // null | "card" | "sealed"
  const [pickedCard, setPickedCard] = useState(null);

  const reload = useCallback(() => {
    fetchInvestments().then(setItems).catch(() => setItems([]));
    fetchSealedProducts().then(setSealedProducts).catch(() => {});
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    if (!items) return [];
    return tab === "tout" ? items : items.filter(i => i.horizon === tab);
  }, [items, tab]);

  const totals = useMemo(() => {
    if (!items) return null;
    let cost = 0, value = 0, known = 0;
    items.forEach(i => {
      const q = i.quantity || 1;
      cost += (i.purchase_price || 0) * q;
      if (i.current_price != null) { value += i.current_price * q; known++; }
    });
    return { cost, value, gain: value - cost, known, total: items.length };
  }, [items]);

  const handleCreate = async (payload) => {
    await createInvestment(payload);
    setAddingKind(null);
    setPickedCard(null);
    reload();
  };
  const handleUpdate = async (id, payload) => {
    const updated = await updateInvestment(id, payload);
    setItems(prev => prev.map(i => i.id === id ? updated : i));
  };
  const handleDelete = async (id) => {
    await deleteInvestment(id);
    setItems(prev => prev.filter(i => i.id !== id));
  };
  const handleCreateSealed = async (payload) => {
    const created = await createSealedProduct(payload);
    setSealedProducts(prev => [...prev, created]);
    return created;
  };
  const handleRecordSealedPrice = async (sealedId, price) => {
    await addSealedPrice(sealedId, price);
    reload();
  };
  const handleUpdateSealed = async (sealedId, payload) => {
    await updateSealedProduct(sealedId, payload);
    reload();
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
        <TrendingUp className="w-6 h-6 text-amber-400" />Investissement
      </h2>
      <p className="text-sm text-zinc-500 mb-5 max-w-2xl">
        Portefeuille à part de ta collection — cartes et produits scellés achetés comme investissement,
        avec prix d'achat, cote suivie, note et horizon. Ne compte dans aucun compteur/complétion de la collection.
      </p>

      {totals && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 bg-zinc-900/60 border border-zinc-800 rounded-xl p-4">
          <Stat label="Investi" value={eur(totals.cost) || "0 €"} />
          <Stat label="Valeur actuelle" value={totals.known > 0 ? eur(totals.value) : "—"} />
          <Stat label="Plus/moins-value" value={totals.known > 0 ? `${totals.gain >= 0 ? "+" : ""}${eur(totals.gain)}` : "—"}
            tone={totals.gain >= 0 ? "text-emerald-400" : "text-red-400"} />
          <Stat label="Lignes" value={`${totals.total} (${totals.known} cotées)`} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["tout", ...HORIZONS].map(h => (
          <button key={h} onClick={() => setTab(h)}
            className={`text-sm px-3 py-1.5 rounded-full border ${tab === h ? "bg-zinc-700 border-zinc-600 text-white" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>
            {h === "tout" ? "Tout" : HORIZON_LABEL[h]}
          </button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="outline" className="border-zinc-700 text-white"
          onClick={() => { setAddingKind(addingKind === "card" ? null : "card"); setPickedCard(null); }}>
          <Plus className="w-4 h-4 mr-1.5" />Carte
        </Button>
        <Button size="sm" variant="outline" className="border-zinc-700 text-white"
          onClick={() => setAddingKind(addingKind === "sealed" ? null : "sealed")}>
          <Plus className="w-4 h-4 mr-1.5" />Scellé
        </Button>
      </div>

      {addingKind === "card" && (
        <div className="mb-4">
          {!pickedCard ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-zinc-400">Chercher la carte à ajouter</span>
                <button onClick={() => setAddingKind(null)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <CardPicker onPick={setPickedCard} />
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 text-sm text-zinc-400 mb-1">
                <CardImage card={pickedCard} quality="low" className="w-7 h-10 object-cover rounded" />
                {pickedCard.name}
                <button onClick={() => setPickedCard(null)} className="text-zinc-500 hover:text-white ml-1">Changer</button>
              </div>
              <AddInvestmentForm kind="card" pickedCard={pickedCard} sealedProducts={sealedProducts}
                onCreateSealed={handleCreateSealed} onSubmit={handleCreate}
                onCancel={() => { setAddingKind(null); setPickedCard(null); }} />
            </div>
          )}
        </div>
      )}

      {addingKind === "sealed" && (
        <div className="mb-4">
          <AddInvestmentForm kind="sealed" sealedProducts={sealedProducts}
            onCreateSealed={handleCreateSealed} onSubmit={handleCreate}
            onCancel={() => setAddingKind(null)} />
        </div>
      )}

      {items === null ? (
        <div className="text-sm text-zinc-500">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-zinc-500 text-center py-10">
          {items.length === 0 ? "Aucun investissement pour l'instant." : "Rien sur cet horizon."}
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map(item => (
            <InvestmentRow key={item.id} item={item} onUpdate={handleUpdate} onDelete={handleDelete}
              onRecordSealedPrice={handleRecordSealedPrice} onUpdateSealed={handleUpdateSealed} />
          ))}
        </div>
      )}
    </div>
  );
};
